/*
 * This file contains before/after hooks for save/remove.
 *
 * These are set up in the schema using the Hooks typedef:
 *
 * hooks: [
 *   {when: 'before', appliesTo: 'save', method: 'handleDocument', async: true},
 *   {when: 'after', appliesTo: 'remove', method: 'cleanupDocument'}
 * ]
 */
import entities from '../entities/index';
import {createRemoteDocument} from "../rest/api";
import {captureRequestSession} from '../Auth/sessionRequest';
import store from "../store/store";
import * as DocumentActions from "../store/actions/documentActions";
import Document from "../entities/document";
import Alert from '../lib/alert';
import * as ContactActions from './actions/contactActions';
import Resource from '../entities/resource';
import paymentApi from '../Financial/PaymentProcessor/paymentApi';
import * as TaskActions from './actions/taskActions';
import {formatFML, formatLFM, formatUniqueName, formatUserName} from "../Contacts/ContactFormat";
import newHRTimestamp from "../lib/picos/timestamp";
import {md5hash} from "../lib/picos/hash";
import Contact from "../entities/contact";
import find from "./find";
import {isDemo} from "../lib/demo";
import {AWAITING_TITLE} from "../TaskManagement/TaskHelper";
import Address from "../entities/address";
import User from "../entities/user";
import type {Hook} from "../@types/schema";
import ImportExport from "../entities/importExport";
import Task from "../entities/task";
import {readBaseVersion, versionRef} from "../lib/staleSave";
import {extractPdfText, readFileIntoDocument} from "../Documents/documentHelper";
import {isRenameOnlyUpload, MAX_LOCAL} from '../Documents/documentUploadRouting';
import {showUploadRefusal, uploadSizeRefusal} from '../Documents/uploadLimits';
import {HttpError} from '../lib/picos/http';

const DEBUG_LOGGING = false;

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId') ?? null;
}

const usesFile = (doc: Document) => !!doc.fileHandle?.name;

/**
 * DocumentEditor loads a remote text or mail document's stored content into the document when it
 * opens it, and sets originalHash from it. That buffer is a copy of the stored object, not data
 * the row holds.
 */
const isEditorLoadedCopy = (doc: Document) => doc.originalHash != null
    && (!!doc.contentType?.startsWith('text/') || doc.contentType === 'message/rfc822');

/**
 * Get before and after hooks for an entity
 *
 * @param {string} entityName - e.g. Document
 * @param {string} appliesTo - 'save' or 'remove'
 * @return {{before: Hook[], after: Hook[]}}
 */
export function getHooks(entityName: string, appliesTo: string): { before: Hook[]; after: Hook[]; } {
    // Sanity check
    if (!entityName) throw new Error('No entity name passed to getHooks()');

    const schemaInfo = entities.schemaInfo(entityName);

    // Sanity check
    if (!schemaInfo) throw new Error(`No schema info found for ${entityName}`);

    const hooks = schemaInfo.hooks || [];

    const before = hooks
        .filter(hook => hook.when === 'before' && hook.appliesTo === appliesTo);

    const after = hooks
        .filter(hook => hook.when === 'after' && hook.appliesTo === appliesTo);

    return {before, after};
}

/**
 * Process import/export documents (load files, save to s3)
 *
 * @param {ImportExport} importExport
 * @return {ImportExport}
 */
export function processImportExport(importExport: ImportExport): ImportExport {
    // This doesn't work, because the sources.documents is a list of UUIDs.  We need the documentHelper version.
    // Perhaps a new transient field on ImportSource for the documentHelper?  How to specify it?  JSON?
    importExport.sources?.forEach(source => {
        // Need to save the documents here, to make it transparent to the UI
        source.fullDocuments.forEach(doc => {
            store.dispatch(DocumentActions.saveDocumentQueue(new Document(doc)));
        });
    });

    return importExport;
}

/**
 * Process a document (load files, save to s3). This function is called just before a document is
 * saved in api.js.  This means the document is currently in the Queue (@see queues.ts). The
 * document may be new or an update.
 *
 * 1. We can attach any type of file to a document. This means unstructured text, structured text,
 *    PDF in ascii-7 or binary format, ZIP files, XML (e.g. docx or xlsw) or literally anything else.
 *    Note that most attachments will be text or html notes, created as Text or Fancy Notes in Outlaw.
 *    Most attachments will be PDFs, followed by Office file formats, pictures and videos.
 * 2. We want small files to be easily and quickly fetched.  Normally we could just put everything into
 *    a blob field or text field in the database, but because hosting costs are based on database size as
 *    a primary factor, we want to use Amazon S3-compliant storage for larger files.  We have set an
 *    arbitrary limit of 5k for what we call "local" files, meaning those that are stored in the
 *    Document object itself, and thus the database.  Remote files will temporarily store the contents
 *    in the Buffer field.
 *
 * @param {Document} doc
 * @return {Promise<Document>}
 */
export async function processDocument(doc: Document, operation = captureRequestSession()): Promise<Document> {
    operation.assertCurrent();
    try {
        if (DEBUG_LOGGING) console.log(`hooks::processDocument ${doc.fileName} (${doc.local? 'local' : 'remote'}) ${doc.fileSize/1024}k ${doc.contentType}`);

        // Create a clone
        let revised = doc.clone();
        // A queued file and rename are one content upload, not a rename-only request.
        if (revised.nameChange && revised.fileHandle instanceof Blob) {
            revised.fileName = revised.newFileName || revised.fileName;
            revised.nameChange = false;
            revised.newFileName = '';
            doc = revised;
        }

        // OWA-1149: a file past its limit is refused before anything below reads it (the PDF text
        // read, the small-file read). The catch shows the refusal as a warning; the 413 holds the
        // queued upload.
        if (usesFile(revised)) {
            const refusal = uploadSizeRefusal(revised.fileName, revised.fileHandle.size, revised.contentType);
            if (refusal) throw new HttpError(413, refusal);
        }

        /**
         * SAVE SMALL TEXT CHANGES WITH NO NAME CHANGE
         *
         * If we are saving text that was input from the DocumentEditor (text or html),
         * and the content is smaller than MAX_LOCAL, there is really nothing to do but
         * move the content into doc.plainText and save the document save normally.
         */
        const content = revised.buffer || revised.plainText || '';

        if (revised.contentType.startsWith('text/') // Text file
            && !revised.nameChange // Not a name change
            && !usesFile(revised) // Not attaching a file
            && !!content // There is content
            && content.length < MAX_LOCAL) // Content can be local
        {
            if (DEBUG_LOGGING) console.log(`hooks::processDocument small text change only`);
            if (!revised.plainText && revised.buffer) {
                revised.plainText = revised.buffer;
                revised.buffer = null;
                if (revised.metadata) revised.metadata = {};
                revised.local = true;
                revised.hash = md5hash(revised.plainText);
            }

            // We are done, return the revised document (no remote saves)
            return revised;
        }

        /**
         * SAVE NAME CHANGE TO EXISTING FILE
         *
         * If this file already exists, is not local, and we have changed the name, just
         * call createRemoteDocument and you are done.
         */
        if (isRenameOnlyUpload(revised)) {
            if (DEBUG_LOGGING) console.log(`hooks::processDocument name change`);
            operation.assertCurrent();
            return createRemoteDocument(revised, false, operation);
        }

        /**
         * OWA-1153: EXTRACT PDF TEXT BEFORE SIZE ROUTING
         *
         * Neither size branch below reads a PDF's text, so do it here for every uploaded
         * PDF. The server derives the document's pii from this plainText. extractPdfText
         * never throws: a scan, a corrupt file or a protected file saves with ''.
         */
        if (revised.contentType === 'application/pdf' && !revised.plainText
            && revised.fileHandle instanceof Blob && revised.fileHandle.size > 0)
        {
            const bytes = new Uint8Array(await revised.fileHandle.arrayBuffer());
            revised.plainText = await extractPdfText(bytes);
        }

        /**
         * READ SMALL FILE INTO PLAINTEXT
         *
         * If there is an associated fileHandle in the document, and the size of the file is
         * less than MAX_LOCAL, we can just read it into the plainText field of the document.
         */
        if (revised.fileHandle?.size && (isDemo() || revised.fileSize <= MAX_LOCAL)) {
            revised.local = true;

            // Read the file into the document
            try {
                const isText = doc.contentType.startsWith('text/');

                const result = await new Promise((resolve, reject) => {
                    const reader = new FileReader();

                    if (isText) reader.readAsText(doc.fileHandle);
                    else reader.readAsDataURL(doc.fileHandle);

                    reader.onload = function () {
                        resolve(reader.result);
                    };

                    reader.onerror = function (error) {
                        reject(error);
                    };
                });

                operation.assertCurrent();
                if (isText) {
                    doc.plainText = result as unknown as string;
                    doc.buffer = null;
                }
                else {
                    const dataUri = result as unknown as string;
                    revised.buffer = dataUri.split(';base64,')[1];

                    return revised;
                }
            }
            catch (err) {
                operation.assertCurrent();
                throw new Error(`The file ${revised.fileName} could not be read: ${err.message}`);
            }
        }

        /**
         * Change: 2025-01-11, version 6.0.1, JCM
         *
         * READ LARGE FILE INTO REMOTE DOCUMENT
         *
         * Finally, if this is a large external file, we will create the document on the server
         * and have it save the contents to AWS S3 storage. The return will have a URI in the
         * document.uri field.
         *
         * Instead of creating a gigantic base64 string and stuffing it into the document, the
         * call to createRemoteDocument will take the fileHandle field of the document and
         * create a FormData object that we will pass to the server. This will chunk it up
         * better than we could do ourselves.
         *
         * The result of createRemoteDocument will have the uri to the remote file in the
         * document.uri field.
         */
        if (usesFile(revised) && revised.fileSize > MAX_LOCAL) {
            operation.assertCurrent();
            revised = await createRemoteDocument(revised, true, operation);
            operation.assertCurrent();
        }
        else {
            // Cast the fileHandle as a FileSystemFileHandle (we know it is not a directory)
            const fh: FileSystemFileHandle = doc.fileHandle as FileSystemFileHandle;

            const content = doc.buffer || doc.plainText || '';

            // If there is a file handle, read it into the buffer.
            // The buffer for revised will be filled with base64-encoded text
            // Version was set in the editor (to 1)
            if (fh?.name) {
                // We use file encoding if the document is greater than MAX_LOCAL in size.
                // Note: This is redundant, we should just be using revised.local.
                revised.encoded = false;
                revised.local = doc.plainText && doc.plainText.length < MAX_LOCAL;

                /* Change: 2024-11-11, version 6.0.1, JCM
                 * We used to read the file into the document's buffer field as a base64
                 * string, but trying to upload a large video makes this a poor choice.
                 * Instead, we now will use FormData and just pass the file in a FormData
                 * structure.
                 */

                revised = await readFileIntoDocument(doc, !doc.local);
                operation.assertCurrent();

                /* New
                 * - Nothing to do here, we already have the fileHandle in the doc's
                 *   fileHandle transient property. This will be used when we call
                 * createRemoteDocument, using the FormData structure
                 */
            }

            // Hash has NOT been set yet!  We can set it now for local files
            if (content) revised.hash = md5hash(content || '');

            // Create a remote document if it is not local, and
            //  a. this is a brand-new document, or
            //  b. the content has changed: an attached file, or editor content that differs
            //     from the content the editor loaded (originalHash).
            // An existing remote document with no file and no originalHash was not loaded into
            // the editor, so this save changes no content (a tag, a folder, privacy). Its
            // plainText is search text and its buffer may be a kept copy, never the file.
            const attachesFile = !!fh?.name;
            const contentChanged = revised.hash !== doc.originalHash
                && (attachesFile || doc.originalHash != null);
            const uploads = !revised.local && (!doc.dbAddedOn || contentChanged);
            const storesNothing = !revised.local && !attachesFile && !uploads;

            /* Change 2024-11-11, version 6.0.1, JCM
             * We now add the hash on the server if the content is not local
             * If not a file, need to determine the version number. Now that we have the new hash,
             * we can check hashes for a change, and increment if necessary.
             *
             * The server stores and signs a remote document at a key that holds its version
             * (services/s3.rs get_s3_path), so a remote document moves to the next version only
             * with the upload that fills it.
             */
            if (!attachesFile && !storesNothing) {
                revised.version = (revised.version || 0) + 1;
            }

            if (uploads) {
                operation.assertCurrent();
                revised = await createRemoteDocument(revised, true, operation);
                operation.assertCurrent();
            }

            if (storesNothing) {
                // Nothing was uploaded: the document keeps the stored object's hash, and the
                // buffer it had unless that is the editor's copy of the stored text.
                revised.hash = doc.hash;
                if (isEditorLoadedCopy(doc)) revised.buffer = '';
            }
            // If this was a remote document, we can dump the buffer (but not the plainText,
            // as that is used for searching!)
            else if (!revised.local) {
                revised.buffer = '';
            }
        }

        // Don't overwrite uri fields!
        revised.uri = revised.uri || doc.uri;
        revised.uriExpires = revised.uriExpires || doc.uriExpires;

        return revised;
    }
    catch (err: any) {
        operation.assertCurrent();
        // OWA-1149: a 413 is a size refusal, held by the queue; it is the person's to act on and its
        // message can name a client's file, so it is a warning that stays out of telemetry.
        if (err instanceof HttpError && err.status === 413) showUploadRefusal(err.message);
        else {
            console.error(err);
            Alert.add(new Alert(Alert.ERROR, doc.id, doc.addedOn, err.clientMessage || err.message));
        }
        throw err;
    }
}

/**
 * This is a safety catch-all to ensure that a task has a title before being saved.
 *
 * @param {Task} task
 * @returns {Task}
 */
export function processTask(task: Task): Task {
    let revised: Task = task.clone();

    if (revised.title == null) revised.title = AWAITING_TITLE;

    return revised;
}

/**
 * Update payment process for a contact or resource. This is required as changes to a contact, or its resources,
 * may make a contact eligible for Payments.  LawPay, for example, requires an email (so that it can send an email
 * when a payment is made).
 *
 * In either case, we are checking to see if we need to generate a processorContactId.
 *
 * @param {Contact|Resource|Address} entity
 * @returns {Promise<Contact|Resource|Address>}
 */
export async function updatePaymentProcessor(entity: Contact | Resource | Address, operation = captureRequestSession()): Promise<Contact | Resource | Address> {
    operation.assertCurrent();
    if (!paymentApi.usesPayments()) return entity;
    if (!entity) throw new Error('Called updatePaymentProcessor hook with no entity');

    // Assign to local variable based on type
    let contact = entity instanceof Contact ? entity : null;
    let resource = entity instanceof Resource ? entity : null;
    let address = entity instanceof Address ? entity : null;
    let c;

    // See if this contact has enough info to store in a payment processor. If not, bail.
    if (contact && !paymentApi.paymentEligible(contact)) return entity;
    else {
        c = find<Contact>('contacts', entity.entityId);
        if (c && !paymentApi.paymentEligible(c)) return entity;
    }

    // Sanity check: ensure we received either a contact or a resource.
    if (!contact && !resource && !address) throw new Error('Did not receive a contact, address or resource in updatePaymentProcessor');

    // Different behavior for LawPay vs Payrix!
    if (paymentApi.isLawpay()) {
        // If this is a contact, and it already has a processorContactId, we are done.
        if (!!contact?.processorContactId) return entity;

        // If this is a resource (mainly email), find the contact and see if it already has a processorContactId, if so, done.
        if (!!resource) {
            // Sanity check: the resource has a related entity
            if (!resource.entityId) throw new Error('Resource did not have a related entityId');

            // Sanity check: the resource's related entity is of type contact
            if (resource.entityType !== 'contact') throw new Error(`Resource's entityType is not a contact [(${resource.entityType} ${resource.entityId}]`);

            // Ok, look up the related contact
            contact = find<Contact>('contacts', resource.entityId);

            // Sanity check: make sure there is a related contact
            if (!contact) {
                console.warn('Resource did not have a related contact', resource.entityId);
                return entity;
            }

            // Finally check for an existing processorContactId
            if (!!contact.processorContactId) return entity;
        }

        // Check to see if this contact is eligible to take payments now
        const eligible = paymentApi.paymentEligible(contact);

        // If yes, update the contact with the processor
        if (eligible) {
            try {
                operation.assertCurrent();
                const lawpayContactStruct = await paymentApi.updateContact(contact);
                operation.assertCurrent();

                if (!!lawpayContactStruct.id && contact) {
                    contact.processorContactId = lawpayContactStruct.id;
                    contact.addedOn = newHRTimestamp();
                    // @ts-ignore
                    contact.addedBy = getCurrentUserId();

                    store.dispatch(ContactActions.saveContactQueue(contact));
                }
            }
            catch (err: any) {
                operation.assertCurrent();
                Alert.add(new Alert(Alert.ERROR, contact?.id || null, contact?.addedOn || 0, err.clientMessage || err.message));
                return entity;
            }
        }
    }
    else if (paymentApi.isPayrix()) {
        // Sync with the contact in Payrix.  If we were passed a contact, use that. Otherwise, use the contact we looked up.
        operation.assertCurrent();
        await paymentApi.syncContacts([contact || c]);
        operation.assertCurrent();
    }

    return entity;
}

/**
 * Precompute fields for contact.
 *
 * @param {Contact} contact
 * @returns {Contact}
 */
export function precomputeContact(contact: Contact): Contact {
    contact.lf = formatLFM(contact, false);
    contact.lfm = formatLFM(contact, true);
    contact.fl = formatFML(contact, false);
    contact.fml = formatFML(contact, true);
    contact.uniqueLFM = formatUniqueName(contact, 'LFM');
    contact.uniqueFML = formatUniqueName(contact, 'FML');

    return contact;
}

/**
 * Precompute fields for user.
 *
 * There are three options: shorten, format and initials.
 * format is LFM or FML.
 * shorten and initials ignores LFM and FML.
 *
 * @param {User} user
 * @returns {User}
 */
export function precomputeUser(user: User): User {
    /* shorten, format, initials */
    user.initials = formatUserName(user, {useInitials: true});
    user.lfm = formatUserName(user, {format: 'LFM'});
    user.fml = formatUserName(user, {format: 'FML'});
    user.shorten = formatUserName(user, {shorten: true});

    return user;
}

/**
 * Track which tasks have already had their milestone bucket fixed this session,
 * to prevent infinite save loops when called from websocket handlers.
 */
const milestoneBucketFixed = new Set<string>();

/**
 * Updates the milestone bucket for a given task.
 *
 * This function checks if the task has a `flatFeeBucket` but does not have a `flatFeeMilestoneBucket`.
 * If so, it retrieves the milestones associated with the `flatFeeBucket` and assigns the first milestone's
 * ID to the task's `flatFeeMilestoneBucket`. It then dispatches an action to save the updated task and
 * processes the queue (once per task per session to avoid feedback loops).
 *
 * @param {Task} task - The task object to update.
 * @returns {Task} - The updated task object.
 */
export function updateMilestoneBucket(task: Task): Task {
    if (task.flatFeeBucket && !task.flatFeeMilestoneBucket) {
        const milestones = store.getState().flatFeeMilestones
            .getMappingFor('flatFeeId', task.flatFeeBucket);

        if (milestones.length) {
            task.flatFeeMilestoneBucket = milestones[0].id;

            // Only dispatch the save once per task to prevent websocket feedback loops:
            // websocket PATCH → hook → saveTaskQueue → server POST → server broadcasts → websocket PATCH → ...
            if (!milestoneBucketFixed.has(task.id)) {
                milestoneBucketFixed.add(task.id);
                // OWA-998: the row was just read from the server, so the save is made from it
                if (readBaseVersion(task.baseVersion).kind === 'absent' && task.dbAddedOn) {
                    task.baseVersion = versionRef(task as Task & {addedOn: number});
                }
                // Must update addedOn/addedBy so the server DAO treats this as a new version
                // (DAO skips INSERT when addedOn matches the existing row)
                task.addedOn = newHRTimestamp();
                task.addedBy = getCurrentUserId();
                // A background repair: refused as stale, it is dropped and redone from the newer row
                store.dispatch(TaskActions.saveTaskQueue(task, undefined, 'reconcile'));
            }
        }
    }

    return task;
}
